import {
  IconNews,
  IconUsers,
  IconNorthStar,
  IconLockOpen,
  IconHomeShare,
  IconCalendarDue,
  IconBuilding,
  IconCrane,
  IconFiles,
  IconBriefcase,
  IconCashBanknote,
} from '@tabler/icons-react';

import { ROUTES } from 'configs/routes';
import { USER_ROLES_ALL, UserEntity, UserRole } from 'entities/User';
import { LogicConfig, LogicShowKey, ShowContentEditorFieldsKey } from 'stores/global/RootStore/AppConfigStore';

import type { MenuItemProps } from './MenuItem/MenuItem';

export const SECTION_TITLE_KEYS = {
  profile: 'profile',
  employees: 'employees',
  clients: 'clients',
  leads: 'leads',
  projects: 'projects',
  meetings: 'meetings',
  about: 'about',
  lots: 'lots',
  documents: 'documents',
  publications: 'publications',
  promotions: 'promotions',
} as const;

export type SectionTitleKey = (typeof SECTION_TITLE_KEYS)[keyof typeof SECTION_TITLE_KEYS];

export const MENU_ITEMS: (MenuItemProps & {
  roles: Set<UserRole>;
  showContentEditor?: ShowContentEditorFieldsKey;
  /** Показывать пункт, только если logic[key].show === true */
  logicShowKey?: LogicShowKey;
  /** Ключ в logic.section_titles для подстановки названия из конфига */
  sectionTitleKey?: SectionTitleKey;
})[] = [
  {
    label: 'Профиль компании',
    Icon: IconBriefcase,
    to: ROUTES.lk.profile.create(),
    roles: USER_ROLES_ALL,
    sectionTitleKey: SECTION_TITLE_KEYS.profile,
  },
  {
    label: 'Сотрудники',
    Icon: IconLockOpen,
    to: ROUTES.lk.accessManagement.employees.create(),
    activePaths: [ROUTES.lk.accessManagement.employees.create(), ROUTES.lk.accessManagement.divisions.create()],
    roles: new Set(['director', 'fixator']),
    sectionTitleKey: SECTION_TITLE_KEYS.employees,
  },
  {
    label: 'Клиенты',
    Icon: IconUsers,
    to: ROUTES.lk.clients.create(),
    roles: USER_ROLES_ALL,
    sectionTitleKey: SECTION_TITLE_KEYS.clients,
  },
  {
    label: 'Заявки',
    Icon: IconNorthStar,
    to: ROUTES.lk.leads.create(),
    roles: USER_ROLES_ALL,
    sectionTitleKey: SECTION_TITLE_KEYS.leads,
  },
  {
    label: 'Проекты',
    Icon: IconBuilding,
    to: ROUTES.lk.projects.create(),
    roles: USER_ROLES_ALL,
    showContentEditor: ShowContentEditorFieldsKey.projects,
    sectionTitleKey: SECTION_TITLE_KEYS.projects,
  },
  {
    label: 'Встречи',
    Icon: IconCalendarDue,
    to: ROUTES.lk.meetings.create(),
    roles: USER_ROLES_ALL,
    sectionTitleKey: SECTION_TITLE_KEYS.meetings,
  },
  {
    label: 'Застройщик',
    Icon: IconCrane,
    to: ROUTES.lk.about.create(),
    roles: USER_ROLES_ALL,
    showContentEditor: ShowContentEditorFieldsKey.about,
    sectionTitleKey: SECTION_TITLE_KEYS.about,
  },
  {
    label: 'Выбор лотов',
    Icon: IconHomeShare,
    to: ROUTES.lk.lots.create(),
    roles: USER_ROLES_ALL,
    sectionTitleKey: SECTION_TITLE_KEYS.lots,
  },
  {
    label: 'Материалы для агентов',
    Icon: IconFiles,
    to: ROUTES.lk.documents.create(),
    roles: USER_ROLES_ALL,
    showContentEditor: ShowContentEditorFieldsKey.documents,
    sectionTitleKey: SECTION_TITLE_KEYS.documents,
  },
  {
    label: 'Акции',
    Icon: IconCashBanknote,
    to: ROUTES.lk.promotions.create(),
    roles: USER_ROLES_ALL,
    sectionTitleKey: SECTION_TITLE_KEYS.promotions,
    logicShowKey: 'promotions',
  },
  {
    label: 'Новости',
    Icon: IconNews,
    to: ROUTES.lk.publications.create(),
    roles: USER_ROLES_ALL,
    sectionTitleKey: SECTION_TITLE_KEYS.publications,
    logicShowKey: 'publications',
  },
];

type MenuItem = (typeof MENU_ITEMS)[number];
export function isMenuItemVisible(item: MenuItem, logic: LogicConfig, user: UserEntity | null): boolean {
  if (item.logicShowKey && !logic[item.logicShowKey]?.show) {
    return false;
  }

  if (!user) {
    return false;
  }

  if (item.showContentEditor && logic.showContentEditor.includes(item.showContentEditor)) {
    return user.isContentEditor;
  }

  return item.roles.has(user.role);
}

type SectionTitles = Record<string, string>;
export function getMenuItemLabel(item: MenuItem, sectionTitles: SectionTitles): string {
  if (item.sectionTitleKey && sectionTitles[item.sectionTitleKey]) {
    return sectionTitles[item.sectionTitleKey];
  }

  return item.label;
}
